import { useCallback, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Article } from "@/data/articles";
import { hasAnalyticsConsent, sendGoogleEvent } from "@/lib/googleTag";
import { getAnalyticsSessionId } from "@/lib/sessionId";

const SCROLL_MILESTONES = [25, 50, 75, 90, 100] as const;
const READ_SCROLL_THRESHOLD = 75;
const READ_COMPLETION_SCROLL_THRESHOLD = 90;
const READ_TIME_THRESHOLD_MS = 30_000;

type ArticleEventName =
  | "article_view"
  | "article_read_start"
  | "article_scroll_depth"
  | "article_read"
  | "article_engagement"
  | "article_share"
  | "article_copy_link"
  | "article_print"
  | "article_font_size_change";

interface ArticleEventPayload {
  eventName: ArticleEventName;
  scrollDepth?: number;
  durationMs?: number;
  method?: string;
  label?: string;
}

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function getTrafficParams() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    page_location: window.location.href,
    page_referrer: document.referrer || undefined,
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
  };
}

export function useArticleAnalytics(article: Article | null | undefined) {
  const startedAtRef = useRef(0);
  const maxScrollRef = useRef(0);
  const sentMilestonesRef = useRef<Set<number>>(new Set());
  const readSentRef = useRef(false);
  const engagementSentRef = useRef(false);

  const baseParams = useMemo(() => {
    if (!article) return null;
    return {
      article_id: article.id,
      article_slug: article.slug,
      article_title: article.title,
      article_category: article.category,
      article_category_label: article.categoryLabel,
      author_name: article.authorName,
      reading_time: article.readingTime,
    };
  }, [article]);

  const persistEvent = useCallback(
    async ({
      eventName,
      scrollDepth,
      durationMs,
      method,
      label,
    }: ArticleEventPayload) => {
      if (!article || !isUuid(article.id)) return;
      // Privacy gate: do NOT write to Supabase when the user has not granted
      // analytics consent. GA4 itself is gated by Consent Mode in the script
      // tag; this guard keeps our own first-party storage symmetric.
      if (!hasAnalyticsConsent()) return;

      const sessionId = getAnalyticsSessionId();
      const { data } = await supabase.auth.getUser();
      const traffic = getTrafficParams();

      const { error: insertError } = await supabase
        .from("article_engagement_events")
        .insert({
          article_id: article.id,
          user_id: data.user?.id || null,
          session_id: sessionId,
          event_name: eventName,
          scroll_depth: scrollDepth ?? null,
          duration_ms: durationMs ?? null,
          source: traffic.utm_source || null,
          referrer: traffic.page_referrer || null,
          metadata: {
            article_slug: article.slug,
            article_title: article.title,
            article_category: article.category,
            article_category_label: article.categoryLabel,
            author_name: article.authorName,
            reading_time: article.readingTime,
            method,
            label,
            page_location: traffic.page_location,
            utm_medium: traffic.utm_medium,
            utm_campaign: traffic.utm_campaign,
          },
        });
      // Analytics is best-effort, but a silent failure hides data loss — surface
      // it to the console so a broken RLS policy or schema drift is diagnosable.
      if (insertError)
        console.warn(
          "[analytics] engagement insert failed:",
          insertError.message,
        );
    },
    [article],
  );

  const trackArticleEvent = useCallback(
    (payload: ArticleEventPayload) => {
      if (!baseParams) return;
      const sessionId = getAnalyticsSessionId();
      const durationMs =
        payload.durationMs ??
        Math.max(0, Date.now() - (startedAtRef.current || Date.now()));
      const scrollDepth = payload.scrollDepth ?? maxScrollRef.current;
      const traffic = getTrafficParams();

      sendGoogleEvent(payload.eventName, {
        ...baseParams,
        ...traffic,
        session_id: sessionId,
        method: payload.method,
        event_label: payload.label,
        scroll_percent: scrollDepth,
        engagement_time_msec: durationMs,
        transport_type: "beacon",
      });

      void persistEvent({ ...payload, scrollDepth, durationMs });
    },
    [baseParams, persistEvent],
  );

  useEffect(() => {
    if (!article || !baseParams) return;

    startedAtRef.current = Date.now();
    maxScrollRef.current = 0;
    sentMilestonesRef.current = new Set();
    readSentRef.current = false;
    engagementSentRef.current = false;

    trackArticleEvent({
      eventName: "article_view",
      scrollDepth: 0,
      durationMs: 0,
    });
    const startTimer = window.setTimeout(() => {
      trackArticleEvent({ eventName: "article_read_start" });
    }, 5_000);

    return () => window.clearTimeout(startTimer);
  }, [article, baseParams, trackArticleEvent]);

  const flushEngagement = useCallback(() => {
    if (!article || engagementSentRef.current) return;
    engagementSentRef.current = true;
    trackArticleEvent({ eventName: "article_engagement" });
  }, [article, trackArticleEvent]);

  useEffect(() => {
    if (!article) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushEngagement();
    };

    window.addEventListener("pagehide", flushEngagement);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      flushEngagement();
      window.removeEventListener("pagehide", flushEngagement);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [article, flushEngagement]);

  const updateScrollDepth = useCallback(
    (scrollDepth: number) => {
      const roundedDepth = Math.max(0, Math.min(100, Math.round(scrollDepth)));
      maxScrollRef.current = Math.max(maxScrollRef.current, roundedDepth);

      for (const milestone of SCROLL_MILESTONES) {
        if (
          roundedDepth >= milestone &&
          !sentMilestonesRef.current.has(milestone)
        ) {
          sentMilestonesRef.current.add(milestone);
          trackArticleEvent({
            eventName: "article_scroll_depth",
            scrollDepth: milestone,
          });
        }
      }

      const durationMs = Date.now() - startedAtRef.current;
      if (
        !readSentRef.current &&
        (roundedDepth >= READ_COMPLETION_SCROLL_THRESHOLD ||
          (roundedDepth >= READ_SCROLL_THRESHOLD &&
            durationMs >= READ_TIME_THRESHOLD_MS))
      ) {
        readSentRef.current = true;
        trackArticleEvent({
          eventName: "article_read",
          scrollDepth: roundedDepth,
          durationMs,
        });
      }
    },
    [trackArticleEvent],
  );

  return { trackArticleEvent, updateScrollDepth };
}
